import React, { Component } from "react";
import mapboxgl from "mapbox-gl";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import type { RouteComponentProps } from "react-router";
import type { Polygon } from "geojson";
import type { PointLike } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { RootState } from "../../reducers/index";
import _ from "lodash";
import {
  INITIAL_INDICATORS,
  BLOCKGROUP_DATA_LAYER,
  LEVEL_LAYERS,
  DRAW_STYLE,
  addVectorSources,
  addLayers,
  highlightScenario,
  addFeatures,
  PARCEL_ZOOM,
  BLOCKGROUP_ZOOM,
  CANOPY_ZOOM,
  LANDUSE_LAYERS,
  INDICATORS,
  gridcodes,
  formatPercent,
} from "../../configs/constants";
import { Blockgroup, Parcel, LEVELS } from "../../types";
import {
  Nav,
  Accordion,
  InputGroup,
  SelectList,
  SelectListItem,
  DataList,
  Legend,
  WelcomePanel,
} from "../../components";
import Scenario from "../Scenario/Scenario";
import Filters from "../Filters/Filters";
import {
  AreaChartOutlined,
  GroupOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import FilterIcon from "../../components/Icons/filterIcon.js";
import intersect from "@turf/intersect";
import booleanWithin from "@turf/boolean-within";
import bbox from "@turf/bbox";
import getArea from "@turf/area";
import { message, Tooltip } from "antd";
import "./Map.css";
import {
  actionUserAdd,
  actionUserGet,
  actionScenarioAdd,
  actionScenarioDelete,
  actionUserAuth,
  actionUserSignOut,
  actionScenarioBlockgroupAdd,
  actionScenarioParcelAdd,
  actionScenarioSave,
  actionScenarioGet,
  actionScenarioReset,
  actionMunicipalitiesGet,
  actionAddFeature,
} from "../../actions/";
import {
  withCommas,
  parcelAddress,
  sqft,
  acre,
  blockgroupBounds,
} from "../../helpers";
import { connect, ConnectedProps } from "react-redux";
import { withRouter } from "react-router-dom";

mapboxgl.accessToken =
  "pk.eyJ1IjoidGVzYS1hcHAiLCJhIjoiY2tmN2RxbmRyMDFlczJybXRlMmtsY2k2eiJ9.yNcU2Z1OSNCCU2PGVP0LNw";

const mapStateToProps = (state: RootState) => {
  if (state) {
    return state;
  } else {
    return null;
  }
};

const mapDispatchToProps = {
  actionUserAdd,
  actionUserGet,
  actionScenarioAdd,
  actionScenarioDelete,
  actionUserAuth,
  actionScenarioBlockgroupAdd,
  actionScenarioParcelAdd,
  actionScenarioSave,
  actionScenarioGet,
  actionScenarioReset,
  actionMunicipalitiesGet,
  actionUserSignOut,
  actionAddFeature,
};

function fixCaps(str: string) {
  return _.startCase(str.toLowerCase()).replace(/Ri/g, "RI");
}

const INITIAL_CENTER: [number, number] = [-71.4, 41.8];
const INITIAL_ZOOM = 11;

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface RouterProps {
  id: string;
}

type TesMapProps = PropsFromRedux & RouteComponentProps<RouterProps>;

type TesMapState = {
  edit: boolean;
  indicators: typeof INITIAL_INDICATORS;
  hoverFeature: any;
  scenarioHover: Blockgroup | Parcel | undefined;
  hasDrawnPopup: boolean | undefined;
  mapLoaded: number;
  lastUpdated: number;
  zoom: number;
  center: [number, number];
  dismissWelcomePanel: boolean;
  scenarioCollapsed: boolean;
  activeBlockgroup?: { properties: Blockgroup } | undefined;
  activeParcel?: { properties: Parcel } | undefined;
};

class TesMap extends Component<TesMapProps, TesMapState> {
  map: mapboxgl.Map;
  draw: any;
  mapRef: React.RefObject<HTMLDivElement>;
  mapContainerRef: React.RefObject<HTMLDivElement>;
  constructor(props: TesMapProps) {
    super(props);
    this.mapRef = React.createRef();
    this.mapContainerRef = React.createRef();
    this.state = {
      edit: false,
      indicators: INITIAL_INDICATORS,
      hoverFeature: null,
      scenarioHover: undefined,
      hasDrawnPopup: null,
      mapLoaded: 0,
      lastUpdated: 0,
      zoom: INITIAL_ZOOM,
      center: INITIAL_CENTER,
      dismissWelcomePanel: false,
      scenarioCollapsed: false,
    };
  }

  componentDidMount() {
    this.props.actionUserGet(this.props.user.id);
    this.getScenario();
    this.props.actionMunicipalitiesGet();
    this.map = new mapboxgl.Map({
      container: this.mapRef.current,
      style: "mapbox://styles/tesa-app/ckf7dsrw807la1amt1fjwh7l5",
      center: this.state.center,
      zoom: this.state.zoom,
      maxPitch: 0,
      dragRotate: false,
      attributionControl: false,
    });
    this.map.addControl(
      new mapboxgl.AttributionControl({
        customAttribution: "American Forests",
      })
    );
    this.map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
      }),
      "top-left"
    );
    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: false,
      },
      styles: DRAW_STYLE,
    });
    this.map.addControl(this.draw, "top-left");
    this.map.once("load", () => {
      addVectorSources(this.map);
      addLayers(this.map);
      this.setState({ mapLoaded: Date.now() });
      this.map.once("load", () => {
        this.setState({ mapLoaded: Date.now() });
      });
    });
    this.map.on("moveend", () => {
      this.setState({
        hoverFeature: null,
        center: this.map.getCenter().toArray() as [number, number],
        zoom: this.map.getZoom(),
      });
    });
    this.map.on("click", (e) => {
      this.onMapClick(e);
    });
    this.map.on("draw.create", (e) => {
      this.onDrawn(e);
    });
    this.map.on("draw.modechange", (e) => {
      if (e.mode === "draw_polygon" && this.map.getZoom() < CANOPY_ZOOM) {
        this.map.zoomTo(CANOPY_ZOOM);
      }
    });

    Object.keys(LEVEL_LAYERS).forEach((level) => {
      for (let layerId of LEVEL_LAYERS[level]) {
        this.map.on("mousemove", layerId, (e) => {
          if (
            this.level() === level ||
            (this.level() === "CANOPY" && level === "PARCEL")
          ) {
            // console.log(e.features[0]);
            this.setState({
              hoverFeature: e.features[0],
            });
          }
        });
      }
    });
    if (!this.props.user.email) {
      this.props.history.push(`/login`);
    }
  }

  onDrawn(e) {
    const drawnFeature = e.features[0];

    // queryRenderedFeatures works with pixels, so convert to them.
    const box = bbox(drawnFeature);
    const projectedBounds: [PointLike, PointLike] = [
      this.map.project([box[0], box[1]]),
      this.map.project([box[2], box[3]]),
    ];

    const centroid: [number, number] = [
      (box[0] + box[2]) / 2,
      (box[1] + box[3]) / 2,
    ];

    const landuseFeatures = this.map.queryRenderedFeatures(projectedBounds, {
      layers: LANDUSE_LAYERS.map((layer) => layer.id),
    });

    // Tally landuse in the custom zone. Maybe unnecessary.
    let landuse = {};
    for (let feature of landuseFeatures.filter(
      (f) => f.geometry.type === "Polygon"
    )) {
      const intersection = intersect(
        feature as unknown as Polygon,
        drawnFeature
      );
      const isWithin = booleanWithin(feature, drawnFeature);
      if (!isWithin && !intersection) continue;
      const geometry = isWithin ? feature : intersection;
      const area = getArea(geometry);
      const landuseType = gridcodes[feature.properties.gridcode];
      landuse[landuseType] = (landuse[landuseType] || 0) + area;
    }

    // Tally blockgroups covered by the zone
    const blockgroupFeatures = this.map.queryRenderedFeatures(projectedBounds, {
      layers: [BLOCKGROUP_DATA_LAYER],
    });
    const blockgroupGroups: { [key: string]: Array<any> } = {};
    for (let feature of blockgroupFeatures.filter(
      (f) => f.geometry.type === "Polygon"
    )) {
      const intersection = intersect(
        feature as unknown as Polygon,
        drawnFeature
      );
      if (!intersection) continue;
      const {
        properties: { GEOID },
      } = feature;
      blockgroupGroups[GEOID] = (blockgroupGroups[GEOID] || []).concat(feature);
    }
    const mainBlockgroup = Object.entries(blockgroupGroups).sort(
      ([_k, a], [_k2, b]) => a.length - b.length
    )[0];
    // This means we can't tell which blockgroup this is in,
    // so it's not clear where it fits.
    if (!mainBlockgroup) return;
    const blockgroupFeature = mainBlockgroup[1][0];

    drawnFeature.properties.id = Date.now();
    drawnFeature.properties.landuse = landuse;
    drawnFeature.properties.name = "Untitled";

    const div = document.createElement("div");
    const header = div.appendChild(document.createElement("div"));
    const description = div.appendChild(document.createElement("div"));
    description.style.paddingBottom = "10px";
    header.style.fontWeight = "700";
    header.style.fontSize = "14px";
    header.innerText = `Custom feature`;
    description.innerText = `Current Canopy: ${withCommas(
      sqft(drawnFeature.properties.landuse.canopy || 0)
    )} sqft`;
    const button = div.appendChild(document.createElement("button"));
    const closeButton = div.appendChild(document.createElement("button"));
    closeButton.className = "ant-btn";
    closeButton.innerText = "Cancel";
    closeButton.style.marginLeft = "10px";
    button.className = "ant-btn ant-btn-primary";
    button.innerText = "Add to Scenario";

    // The events for this draw code and the code for clicking
    // on parcels tends to fight, hence this… lock-like construct,
    // which lets us temporarily remove parcel clicking until
    // custom area drawn popups are dismissed.
    this.setState({ hasDrawnPopup: true });
    setTimeout(() => {
      const popup = new mapboxgl.Popup({
        closeOnMove: false,
        closeOnClick: false,
        closeButton: false,
      })
        .setLngLat(centroid)
        .setDOMContent(div)
        .addTo(this.map);

      popup.on("close", () => {
        this.setState({ hasDrawnPopup: false });
      });

      closeButton.addEventListener("click", () => {
        this.setState({ hasDrawnPopup: false });
        this.draw.delete(drawnFeature.id);
        popup.remove();
      });

      button.addEventListener("click", () => {
        this.setState({ hasDrawnPopup: false });
        popup.remove();
        this.props.actionAddFeature({
          value: drawnFeature,
          blockgroup: blockgroupFeature.properties,
          municipalityId: blockgroupFeature.properties.NAME,
          municipality: this.props.municipalities.find(
            (item) => item.featureId === blockgroupFeature.properties.NAME
          ),
          blockgroupId: blockgroupFeature.properties.GEOID,
        });

        this.props.actionScenarioSave(this.props.scenario);
        this.draw.delete(drawnFeature.id);
      });
    }, 100);
  }

  level(): LEVELS {
    const { zoom } = this.state;
    if (zoom < BLOCKGROUP_ZOOM) {
      return "MUNICIPALITY";
    } else if (zoom >= BLOCKGROUP_ZOOM && zoom < PARCEL_ZOOM) {
      return "BLOCKGROUP";
    } else if (zoom >= PARCEL_ZOOM && zoom < CANOPY_ZOOM) {
      return "PARCEL";
    } else if (zoom >= CANOPY_ZOOM) {
      return "CANOPY";
    }
  }

  activeLayer() {
    return this.state.indicators[this.level()];
  }

  setLayer(level: LEVELS, value: any) {
    this.setState(
      (state) => ({
        indicators: {
          ...state.indicators,
          [level]: value,
        },
      }),
      () => {
        let expression = this.state.indicators[level].expression;
        for (let layerId of LEVEL_LAYERS[level]) {
          for (let [prop, val] of Object.entries(expression)) {
            this.map.setPaintProperty(layerId, prop, val);
          }
        }
      }
    );
  }

  onMapClick(e) {
    if (this.draw.getMode() !== "simple_select" || this.state.hasDrawnPopup) {
      return;
    }
    if (!this.props.scenario?.id && this.state.zoom >= BLOCKGROUP_ZOOM) {
      message.warning("Create a scenario to start adding.");
      return;
    }
    if (this.state.zoom <= BLOCKGROUP_ZOOM) {
      const div = document.createElement("div");
      const header = div.appendChild(document.createElement("div"));
      const description = div.appendChild(document.createElement("div"));
      description.classList.add("mapboxgl-popup-description");
      header.classList.add(
        "mapboxgl-popup-header",
        "mapboxgl-popup-header--align-center"
      );

      let [munifeatures] = this.map.queryRenderedFeatures(e.point, {
        layers: LEVEL_LAYERS.MUNICIPALITY,
      });
      header.innerText = munifeatures["properties"]["NAME"];
      const button = div.appendChild(document.createElement("button"));
      const closeButton = div.appendChild(document.createElement("button"));
      closeButton.className = "ant-btn";
      closeButton.innerText = "Close";
      closeButton.style.marginLeft = "10px";
      button.className = "ant-btn ant-btn-primary";
      button.innerText = "View City Report";

      const popup = new mapboxgl.Popup({
        closeOnMove: true,
        closeButton: false,
        closeOnClick: true,
      })
        .setLngLat(e.lngLat)
        .setDOMContent(div)
        .addTo(this.map);

      closeButton.addEventListener("click", () => {
        popup.remove();
      });

      button.addEventListener("click", () => {
        let url =
          window.location.origin +
          `/${munifeatures["properties"]["NAME"]
            .toLowerCase()
            .replaceAll(" ", "_")}/report/`;
        window.open(url, "_blank");
      });
      return;
    }

    let [blockgroupFeature] = this.map.queryRenderedFeatures(e.point, {
      layers: [BLOCKGROUP_DATA_LAYER],
    });

    let parcelFeature = this.map.queryRenderedFeatures(e.point, {
      layers: LEVEL_LAYERS.PARCEL,
    })[0] as unknown as { properties: Parcel };

    const selectingParcel = this.state.zoom >= PARCEL_ZOOM;

    if (!blockgroupFeature) return;
    if (selectingParcel && !parcelFeature) return;

    const div = document.createElement("div");
    const header = div.appendChild(document.createElement("div"));
    const description = div.appendChild(document.createElement("div"));
    description.classList.add("mapboxgl-popup-description");
    header.classList.add("mapboxgl-popup-header");
    header.innerText = selectingParcel
      ? parcelAddress(parcelFeature.properties)
      : `Block Group ${blockgroupFeature.properties.GEOID}`;
    description.innerText = selectingParcel
      ? `Possible tree canopy: ${withCommas(
          sqft(parcelFeature.properties.PossibleTC)
        )} sq ft.`
      : `Possible tree canopy: ${Math.round(
          acre(blockgroupFeature.properties.PossibleTC.toFixed())
        )} acres.`;
    const button = div.appendChild(document.createElement("button"));
    const closeButton = div.appendChild(document.createElement("button"));
    closeButton.className = "ant-btn";
    closeButton.innerText = "Close";
    closeButton.style.marginLeft = "10px";
    button.className = "ant-btn ant-btn-primary";
    button.innerText = "Add to Scenario";

    const popup = new mapboxgl.Popup({
      closeOnMove: true,
      closeButton: false,
      closeOnClick: true,
    })
      .setLngLat(e.lngLat)
      .setDOMContent(div)
      .addTo(this.map);

    closeButton.addEventListener("click", () => {
      popup.remove();
    });

    button.addEventListener("click", () => {
      if (this.state.zoom > BLOCKGROUP_ZOOM && this.state.zoom <= PARCEL_ZOOM) {
        this.props.actionScenarioBlockgroupAdd({
          id: blockgroupFeature.properties.GEOID,
          value: blockgroupFeature.properties,
          municipalityId: blockgroupFeature.properties.incorpname,
          municipality: this.props.municipalities.find(
            (item) => item.featureId === blockgroupFeature.properties.incorpname
          ),
        });
      }
      if (this.state.zoom > PARCEL_ZOOM) {
        this.props.actionScenarioParcelAdd({
          id: parcelFeature.properties.PARCEL_ID,
          value: parcelFeature.properties,
          blockgroup: blockgroupFeature.properties,
          municipalityId: parcelFeature.properties.Municipali,
          municipality: this.props.municipalities.find(
            (item) => item.featureId === parcelFeature.properties.Municipali
          ),
          blockgroupId: parcelFeature.properties.BGID,
        });
      }
      popup.remove();
      this.props.actionScenarioSave(this.props.scenario);
      setTimeout(() => {
        let el;
        if (this.state.activeParcel) {
          el = document.getElementById(this.state.activeParcel.properties.BGID);
        }
        if (this.state.activeBlockgroup) {
          el = document.getElementById(
            this.state.activeBlockgroup.properties.GEOID
          );
        }
        if (el) {
          const elems = document.querySelectorAll("div.active");
          elems.forEach((el) => el.classList.remove("active"));
          el.classList.add("active");
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 1000);
    });
  }

  async getScenario() {
    if (!this.props.match.params.id) {
      this.props.actionScenarioReset();
      return;
    }
    await this.props.actionScenarioGet({
      id: this.props.match.params.id,
    });
    this.setState({
      scenarioCollapsed: false,
    });
    message.success("Scenario loaded");
  }

  highlight() {
    const { scenario } = this.props;
    if (!this.map.loaded()) return;
    highlightScenario(this.map, scenario, this.state.scenarioHover);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.props.match.params.id) {
      this.getScenario();
    }
    if (this.map.loaded()) {
      const { scenario } = this.props;
      this.highlight();
      addFeatures(this.map, scenario);

      if (scenario && scenario.id !== prevProps.scenario?.id) {
        const ids = _.flatMapDeep(scenario.municipalities, (municipality) =>
          municipality.blockgroups.map((blockgroup) => blockgroup.GEOID)
        );
        if (ids.length) {
          this.map.fitBounds(blockgroupBounds(ids), { padding: 50 });
        }
      }
    }
  }

  scenarioAdd = async () => {
    const res: any = await this.props.actionScenarioAdd({
      name: "My New Scenario",
      users: [this.props.user.id],
    });
    this.props.history.push(`/scenario/${res.payload.data.id}`);
  };

  scenarioDelete = async () => {
    await this.props.actionScenarioDelete({
      id: this.props.scenario.id,
    });
    this.setState({ scenarioCollapsed: false });
    this.props.history.push(`/`);
  };

  render() {
    const { hoverFeature } = this.state;
    return (
      <>
        <Nav {...this.props} />
        <div
          id="te-map"
          className={`map ${this.props.scenario?.id ? "" : "hide-draw"}`}
          ref={this.mapContainerRef}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
            }}
            ref={this.mapRef}
          ></div>
          <div className="controls">
            <div className="controls__tab-row">
              {[
                ["Municipality", "City", 9],
                ["Blockgroup", "Block Group", BLOCKGROUP_ZOOM + 1],
                ["Parcel", "Parcel", PARCEL_ZOOM + 1],
                ["Canopy", "Canopy", CANOPY_ZOOM],
              ].map(([level, label, zoom]: [string, string, number]) => (
                <button
                  key={level}
                  onClick={() => this.map.zoomTo(zoom)}
                  className={`controls__tab controls__tab--${level.toLowerCase()} ${
                    this.level() === level.toUpperCase()
                      ? "controls__tab--active"
                      : ""
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="controls__tab-content">
              <Accordion icon={<AreaChartOutlined />} heading="Info Panel">
                <div className="total-list">
                  <div
                    className={
                      hoverFeature
                        ? "total-list__value"
                        : "total-list__value total-list__value--disabled"
                    }
                  >
                    {hoverFeature ? (
                      <>
                        {this.activeLayer().format(
                          hoverFeature.properties[
                            this.state.indicators[this.level()].field
                          ]
                        )}
                      </>
                    ) : (
                      <>0</>
                    )}
                  </div>
                  <div
                    className={
                      hoverFeature
                        ? "total-list__label"
                        : "total-list__label total-list__label--disabled"
                    }
                  >
                    {hoverFeature ? (
                      <>
                        <span>{this.activeLayer().label}</span>
                        {this.activeLayer().explanation && (
                          <Tooltip
                            placement="topLeft"
                            arrowPointAtCenter
                            title={this.activeLayer().explanation}
                          >
                            <QuestionCircleOutlined className="icon icon--interactive total-list__label-icon" />
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <>---</>
                    )}
                  </div>
                  <div className="total-list__location">
                    {hoverFeature &&
                      (this.level() === "MUNICIPALITY" ? (
                        <>{fixCaps(hoverFeature.properties.NAME)}</>
                      ) : this.level() === "BLOCKGROUP" ? (
                        <>
                          {`${fixCaps(hoverFeature.properties.NAME)} | ${
                            hoverFeature.properties.GEOID
                          }`}
                        </>
                      ) : this.level() === "PARCEL" ||
                        this.level() === "CANOPY" ? (
                        <>
                          {hoverFeature.properties.ADDR},{" "}
                          {hoverFeature.properties.CITY}
                          <br />
                          Area:{" "}
                          {withCommas(
                            Math.round(
                              hoverFeature.properties.TotalAcres * 43560
                            )
                          )}{" "}
                          sq ft
                        </>
                      ) : null)}
                  </div>
                </div>
                <InputGroup label="Legend" type="primary" alignment="centered">
                  <Legend indicator={this.state.indicators[this.level()]} />
                </InputGroup>
                {hoverFeature &&
                this.level() === "BLOCKGROUP" &&
                this.state.indicators.BLOCKGROUP ===
                  INDICATORS.TREE_EQUITY_SCORE ? (
                  <>
                    <DataList
                      heading="Score Indicators"
                      items={[
                        {
                          label: "Current canopy cover",
                          value: formatPercent(
                            hoverFeature.properties[
                              INDICATORS.TREE_CANOPY_PERCENT.field
                            ]
                          ),
                        },
                        {
                          label: "Canopy cover goal",
                          value: formatPercent(
                            hoverFeature.properties[
                              INDICATORS.TREE_CANOPY_GOAL.field
                            ]
                          ),
                        },
                      ]}
                    />
                    <DataList
                      heading="Priority Index"
                      items={[
                        INDICATORS.PEOPLE_OF_COLOR,
                        INDICATORS.POVERTY_PERCENT,
                        INDICATORS.UNEMPLOYMENT_RATE,
                        INDICATORS.CHILDREN_PERCENT,
                        INDICATORS.SENIOR_PERCENT,
                        INDICATORS.TEMP,
                        INDICATORS.HEALTH_NORM,
                      ]
                        .map((indicator) => {
                          const value =
                            hoverFeature.properties[indicator.field];
                          if (value !== undefined) {
                            return {
                              label: indicator.label,
                              value: indicator.format(value),
                            };
                          }
                          return false;
                        })
                        .filter(Boolean)}
                      alignment="left"
                    />
                  </>
                ) : null}
              </Accordion>

              <Accordion
                icon={<GroupOutlined />}
                heading="Indicator Layers"
                collapsed
              >
                {this.level() === "MUNICIPALITY" && (
                  <SelectList>
                    {[
                      INDICATORS.MUNICIPALITY_TREE_EQUITY_SCORE,
                      INDICATORS.TREE_CANOPY_POSSIBLE_PERCENT,
                      INDICATORS.TREE_CANOPY_PERCENT,
                    ].map((indicator) => (
                      <SelectListItem
                        key={indicator.field}
                        label={indicator.label}
                        onClick={() => this.setLayer("MUNICIPALITY", indicator)}
                        isActive={
                          this.state.indicators.MUNICIPALITY === indicator
                        }
                        tooltip={indicator.explanation}
                      />
                    ))}
                  </SelectList>
                )}
                {this.level() === "BLOCKGROUP" && (
                  <>
                    <SelectList>
                      <SelectListItem label="Tree Equity" isHeading />
                      {[
                        INDICATORS.TREE_EQUITY_SCORE,
                        INDICATORS.TREE_CANOPY_PERCENT,
                        INDICATORS.TREE_CANOPY_GAP,
                      ].map((indicator, i) => (
                        <SelectListItem
                          key={`equity-${i}`}
                          label={indicator.label}
                          onClick={() => this.setLayer("BLOCKGROUP", indicator)}
                          isActive={
                            this.state.indicators.BLOCKGROUP === indicator
                          }
                          tooltip={indicator.explanation}
                        />
                      ))}
                    </SelectList>

                    <SelectList>
                      <SelectListItem label="Demographics" isHeading />
                      {[
                        INDICATORS.POVERTY_PERCENT,
                        INDICATORS.PEOPLE_OF_COLOR,
                        INDICATORS.CHILDREN_PERCENT,
                        INDICATORS.SENIOR_PERCENT,
                        INDICATORS.HEALTH_NORM,
                      ].map((indicator, i) => (
                        <SelectListItem
                          key={`equity-${i}`}
                          label={indicator.label}
                          onClick={() => this.setLayer("BLOCKGROUP", indicator)}
                          isActive={
                            this.state.indicators.BLOCKGROUP === indicator
                          }
                          tooltip={indicator.explanation}
                        />
                      ))}
                    </SelectList>

                    <SelectList>
                      <SelectListItem label="Climate" isHeading />
                      {[
                        INDICATORS.HEALTH_EQUITY_ZONE,
                        INDICATORS.CLIMATE_HEALTH_RISK,
                        INDICATORS.TEMP,
                        INDICATORS.AFTER_AIR,
                        INDICATORS.EVE_AIR,
                      ].map((indicator, i) => (
                        <SelectListItem
                          key={`equity-${i}`}
                          label={indicator.label}
                          onClick={() => this.setLayer("BLOCKGROUP", indicator)}
                          isActive={
                            this.state.indicators.BLOCKGROUP === indicator
                          }
                          tooltip={indicator.explanation}
                        />
                      ))}
                    </SelectList>
                  </>
                )}
                {(this.level() === "PARCEL" || this.level() === "CANOPY") && (
                  <SelectList>
                    {[
                      INDICATORS.PARCEL_TREE_CANOPY_PERCENT,
                      INDICATORS.TREE_CANOPY_POSSIBLE,
                      INDICATORS.FLOOD_RISK,
                      INDICATORS.PUBLIC,
                      // PARCEL_INDICATORS.HEALTH_EQUITY_ZONE,
                    ].map((indicator, i) => (
                      <SelectListItem
                        key={`equity-${i}`}
                        label={indicator.label}
                        onClick={() => this.setLayer("PARCEL", indicator)}
                        isActive={this.state.indicators.PARCEL === indicator}
                        tooltip={indicator.explanation}
                      />
                    ))}
                  </SelectList>
                )}
              </Accordion>

              <Accordion icon={<FilterIcon />} heading="Filters" collapsed>
                <Filters map={this.map} level={this.level()} {...this.props} />
              </Accordion>
            </div>
          </div>

          {this.map &&
            this.state.mapLoaded &&
            (this.props.scenario?.id ? (
              <Scenario
                map={this.map}
                draw={this.draw}
                scenarioDelete={this.scenarioDelete}
                hover={(item) => {
                  if (!this.map.loaded()) return;
                  if (this.state.scenarioHover !== item) {
                    this.setState({ scenarioHover: item }, () => {
                      this.highlight();
                    });
                  }
                }}
                close={() => {
                  this.props.history.push(`/`);
                }}
                collapsed={this.state.scenarioCollapsed}
                toggleCollapse={() =>
                  this.setState(({ scenarioCollapsed }) => ({
                    scenarioCollapsed: !scenarioCollapsed,
                  }))
                }
              />
            ) : !this.state.dismissWelcomePanel ? (
              <WelcomePanel
                scenarioAdd={this.scenarioAdd}
                close={() => this.setState({ dismissWelcomePanel: true })}
              />
            ) : null)}
        </div>
      </>
    );
  }
}

const TesMapContainer = connector(TesMap);

export default withRouter(TesMapContainer);
